"use client"

import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useEffect, useRef } from "react"
import { useInView } from "react-intersection-observer"
import { mediaQueries, useInViewConfig, useTransitionUpStyles } from "ui"
import { colors, sizes } from "ui/theme/theme"
import BlockWrapper from "../../common/BlockWrapper"
import CommonTitle from "../../common/bit/CommonTitle"
import BlockCardContainerWithShape from "../CopyOfPolicyBlock/BlockCardContainerWithShape"
import FailureState from "../CopyOfPolicyBlock/FailureState"
import OTPForm from "../CopyOfPolicyBlock/OTPForm"
import SuccessState from "../CopyOfPolicyBlock/SuccessState"
import AccidentReportForm from "./AccidentReportForm"
import CarDetailsForm from "./CarDetailsForm"
import CarInsuranceDetails from "./CarInsuranceDetails"
import useThirdPartyCoverageFlow from "./useThirdPartyCoverageFlow"

const StyledBlockWrapper = styled(BlockWrapper)`
  z-index: 2 !important;
`

// Styled components
const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 80px;
  padding-bottom: 64px;

  ${mediaQueries.desktop} {
    padding-top: 120px;
    padding-bottom: 120px;
  }
`

const StyledFormCard = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 764px;
  margin: 0 auto;
  padding: ${sizes.baseTriple};
  border-radius: 30px;
  background-color: ${colors.white};
  gap: ${sizes.baseDouble};
  box-shadow: -4px 8px 30px 0px rgba(2, 1, 64, 0.12);
  position: relative;
  margin-top: 70px;
  margin-bottom: 40px;

  ${mediaQueries.desktop} {
    padding: ${sizes.baseSix};
    margin-top: 70px;
  }
`

const StyledShapeWrapper = styled.div`
  display: none;
  ${mediaQueries.desktop} {
    display: block;
    position: absolute;
    right: 50px;
    top: -70px;
    width: 100%;
    z-index: -1;
    height: 622px;
    width: 629px;
    top: -70px;
  }
`

const StyledStatusErrorMessage = styled(Typography)`
  color: ${colors.black};
  font-weight: 700;
  font-size: 22px;
`

const StyledFormCardNotFound = styled(StyledFormCard)`
  border: 1px solid ${colors.lightRed};
  text-align: center;
  ${mediaQueries.desktop} {
    padding: ${sizes.baseTriple};
    border-radius: 20px;
  }
`

const StyledBlockCardContainerWithShape = styled(BlockCardContainerWithShape)`
  padding-bottom: 60px;
  padding-top: 80px;
  ${mediaQueries.desktop} {
    padding: 140px;
  }
`
const StyledTitle = styled(Typography)`
  font-size: 38px;
  font-weight: 700;
  text-align: center;
  color: ${colors.black};
  margin-top: 32px;
  ${mediaQueries.desktop} {
    margin-top: 60px;
  }
  ${mediaQueries.mobile} {
    font-size: 31px;
  }
`

const StyledFormAccident = styled(StyledFormCard)`
  margin-top: 40px;
`

// Main component
interface ThirdPartyCoverageBlockProps {
  data: any
  id?: string
}

const ThirdPartyCoverageBlock = ({
  data,
  id,
}: ThirdPartyCoverageBlockProps) => {
  const t = useTranslations("ThirdPartyCoverage")
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)

  // Use the custom hook for form state and handlers
  const {
    formState,
    carDetails,
    phoneNumber,
    otpError,
    carRepaired,
    accidentReportData,
    statusMessage,
    otpAttempts,
    handleCarDetailsSubmit,
    handleInsuranceDetailsSubmit,
    handleAccidentReportSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleRetry,
  } = useThirdPartyCoverageFlow()
  const formRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (
      formRef.current &&
      (formState === "carDetailsSubmitted" ||
        formState === "accidentFormSubmitted")
    ) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [formState])

  // If there's a final state, show the appropriate component
  if (formState === "success" || formState === "otpVerified") {
    return (
      <StyledBlockCardContainerWithShape id={id}>
        <SuccessState data={data} />
      </StyledBlockCardContainerWithShape>
    )
  }

  if (formState === "error") {
    return (
      <StyledBlockCardContainerWithShape id={id}>
        <FailureState data={data} onRetry={handleRetry} />
      </StyledBlockCardContainerWithShape>
    )
  }

  // Show OTP form when needed, replacing the main form sequence
  if (
    formState === "otpSent" ||
    formState === "otpVerifying" ||
    formState === "otpError" ||
    formState === "otpExpired"
  ) {
    return (
      <StyledBlockWrapper id={id}>
        <StyledContainer ref={ref} style={transitionStyles}>
          <CommonTitle
            title={data?.title?.value}
            subtitle={data?.subtitle?.value}
            titleComponent={"h1"}
          />
          <StyledFormCard ref={formRef}>
            <OTPForm
              data={data}
              phoneNumber={phoneNumber}
              onSubmit={handleOtpSubmit}
              onResend={handleResendOtp}
              isSubmitting={formState === "otpVerifying"}
              error={otpError || undefined}
              otpAttempts={otpAttempts}
              numberOfDigits={5}
            />
            <StyledShapeWrapper>
              <Image
                fill
                src={"/formBackgroundShape.svg"}
                alt="form Background Shape"
              />
            </StyledShapeWrapper>
          </StyledFormCard>
        </StyledContainer>
      </StyledBlockWrapper>
    )
  }

  return (
    <StyledBlockWrapper id={id}>
      <StyledContainer ref={ref} style={transitionStyles}>
        <CommonTitle
          title={data.title.value}
          subtitle={data.subtitle.value}
          titleComponent={"h1"}
        />

        {/* Car Details Form - First Step */}
        <StyledFormCard>
          <CarDetailsForm
            data={data}
            onSubmit={handleCarDetailsSubmit}
            isSubmitting={formState === "checking"}
            disabled={formState === "checking"}
          />
          <StyledShapeWrapper>
            <Image
              fill
              src={"/formBackgroundShape.svg"}
              alt="form Background Shape"
            />
          </StyledShapeWrapper>
        </StyledFormCard>

        {/* Show car not found card below the form when relevant */}
        {formState === "carNotFound" && statusMessage && (
          <StyledFormCardNotFound>
            <StyledStatusErrorMessage>{statusMessage}</StyledStatusErrorMessage>
          </StyledFormCardNotFound>
        )}

        {/* Insurance Details - Second Step */}
        {(formState === "carDetailsSubmitted" ||
          formState === "accidentFormSubmitted") &&
          carDetails && (
            <StyledFormCard ref={formRef}>
              <CarInsuranceDetails
                data={data}
                carDetails={carDetails}
                onSubmit={handleInsuranceDetailsSubmit}
                disabled={formState !== "carDetailsSubmitted"}
              />
            </StyledFormCard>
          )}

        {/* Accident Report Form - Third Step (after insurance details, before OTP) */}
        {formState === "accidentFormSubmitted" && (
          <>
            <StyledTitle>{data?.accidentFormTitle?.value}</StyledTitle>
            <StyledFormAccident ref={formRef}>
              <AccidentReportForm
                data={data}
                onSubmit={handleAccidentReportSubmit}
                isSubmitting={false}
                disabled={formState !== "accidentFormSubmitted"}
              />
            </StyledFormAccident>
          </>
        )}
      </StyledContainer>
    </StyledBlockWrapper>
  )
}

export default ThirdPartyCoverageBlock
